import { useMutation, useQuery } from '@tanstack/vue-query'

import type { ApiResponse } from '@/types/user'

import { useAxios } from '@/composables/use-axios'

/**
 * AAN (Attribuly Audiences Network) API
 * 基于老项目 ecom-attribuly/examples/app-shopify/src/api/user.js 中的接口
 */

const API_BASE_URL_V2_1 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/`

/**
 * Type definitions
 */

// AAN Question List 响应数据
export interface AanQuestionOption {
  id: number
  question: string
  disabled?: boolean
}

export interface AanQuestionListResponse {
  records: AanQuestionOption[]
  selected: number[]
}

// AAN Question Answer 请求数据
export interface AanQuestionAnswerRequest {
  answers: Array<{
    id: number
    checked: boolean
  }>
}

// AAN Feature List 响应数据
export interface AanFeatureListResponse {
  aan_status: 'open' | 'close' | 'closed' | string
  max_emails: number
  trigger_rules: string | TriggerRulesData | null
  vvjs?: string
}

// Trigger Rules 结构
export interface TriggerCondition {
  field: string
  operator: string
  target: number
}

export interface TriggerGroup {
  all_of?: (TriggerCondition | TriggerGroup)[]
  any_of?: (TriggerCondition | TriggerGroup)[]
}

export type TriggerRulesData = TriggerGroup

// AAN Save Config 请求数据
export interface AanSaveConfigRequest {
  config: {
    aan_status: 'open' | 'close' | string
    max_emails: number
    trigger_rules: string
  }
}

// 通用Mutation响应
export interface MutationResponse<T = unknown> {
  code: number
  message: string
  data?: T
}

/**
 * 获取AAN问题列表
 * 对应老项目: aanQuestionList
 * 接口: GET /v2-1/aan/question-list
 */
export function useAanQuestionList() {
  const { axiosInstance } = useAxios()
  return useQuery({
    queryKey: ['aanQuestionList'],
    queryFn: async (): Promise<AanQuestionListResponse> => {
      const response = await axiosInstance.get<ApiResponse<AanQuestionListResponse>>(`${API_BASE_URL_V2_1}aan/question-list`)
      return response.data?.data
    },
  })
}

// 获取AAN问题列表 - 普通函数版本
export async function aanQuestionListApi(): Promise<AanQuestionListResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<AanQuestionListResponse>>(`${API_BASE_URL_V2_1}aan/question-list`)
  return response.data?.data
}

/**
 * 保存AAN问题答案
 * 对应老项目: aanQuestionAnswer
 * 接口: POST /v2-1/aan/question-answer
 */
export function useAanQuestionAnswer() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['aanQuestionAnswer'],
    mutationFn: async (data: AanQuestionAnswerRequest): Promise<MutationResponse> => {
      const response = await axiosInstance.post<MutationResponse>(`${API_BASE_URL_V2_1}aan/question-answer`, data)
      return response.data
    },
  })
}

// 保存AAN问题答案 - 普通函数版本
export async function aanQuestionAnswerApi(data: AanQuestionAnswerRequest): Promise<MutationResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<MutationResponse>(`${API_BASE_URL_V2_1}aan/question-answer`, data)
  return response.data
}

/**
 * 获取AAN功能配置列表
 * 对应老项目: aanFeatureList
 * 接口: POST /v2-1/frontend/config/get_list
 */
export function useAanFeatureList() {
  const { axiosInstance } = useAxios()
  return useQuery({
    queryKey: ['aanFeatureList'],
    queryFn: async (): Promise<AanFeatureListResponse> => {
      const response = await axiosInstance.post<ApiResponse<AanFeatureListResponse>>(`${API_BASE_URL_V2_1}frontend/config/get_list`)
      return response.data?.data
    },
    refetchOnWindowFocus: false,
  })
}

// 获取AAN功能配置 - 普通函数版本
export async function aanFeatureListApi(): Promise<AanFeatureListResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<AanFeatureListResponse>>(`${API_BASE_URL_V2_1}frontend/config/get_list`)
  return response.data?.data
}

/**
 * 保存AAN配置
 * 对应老项目: aanSaveConfig
 * 接口: POST /v2-1/frontend/config/save
 */
export function useAanSaveConfig() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['aanSaveConfig'],
    mutationFn: async (data: AanSaveConfigRequest): Promise<MutationResponse> => {
      const response = await axiosInstance.post<MutationResponse>(`${API_BASE_URL_V2_1}frontend/config/save`, data)
      return response.data
    },
  })
}

// 保存AAN配置 - 普通函数版本
export async function aanSaveConfigApi(data: AanSaveConfigRequest): Promise<MutationResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<MutationResponse>(`${API_BASE_URL_V2_1}frontend/config/save`, data)
  return response.data
}

/**
 * 旧版本的Opt-in保存API（兼容用）
 * @deprecated 请使用 useAanQuestionAnswer
 */
export function useAanOptInSave() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['aanOptInSave'],
    mutationFn: async (data: { enabled: boolean }): Promise<MutationResponse> => {
      // 这个是兼容旧代码的，实际上新系统中用 useAanQuestionAnswer
      const response = await axiosInstance.post<MutationResponse>(`${API_BASE_URL_V2_1}aan/question-answer`, {
        answers: [{ id: 1, checked: data.enabled }],
      })
      return response.data
    },
  })
}
